import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, TextField, MenuItem, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Checkbox, CircularProgress, Alert, IconButton, Breadcrumbs, Link,
  InputAdornment
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Save as SaveIcon, 
  ArrowBack as BackIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import tripService from '../services/tripService';
import cityService from '../services/cityService';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const TripCreatePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadOrders(selectedCity);
    } else {
      setOrders([]);
      setSelectedOrders([]);
    }
  }, [selectedCity]);

  const loadCities = async () => {
    try {
      setLoadingCities(true);
      const data = await cityService.getCities({
        paginate: 'false',
        is_available_for_trips: 'true',
        active_only: 'true'
      });
      setCities(data.results || data);
    } catch (err) {
      setError('Error al cargar las ciudades disponibles.');
    } finally {
      setLoadingCities(false);
    }
  };

  const loadOrders = async (city) => {
    try {
      setLoadingOrders(true);
      const data = await tripService.getPendingOrdersByCity(city);
      setOrders(data);
      // Seleccionar todos por defecto
      setSelectedOrders(data.map(o => o.id));
    } catch (err) {
      setError('Error al cargar los pedidos de la ciudad seleccionada.');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleToggleOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleCreateTrip = async () => {
    if (!selectedCity) {
      setError('Debe seleccionar una ciudad.');
      return;
    }
    if (selectedOrders.length === 0) {
      setError('Debe seleccionar al menos un pedido para el viaje.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const newTrip = await tripService.createTrip({
        city: selectedCity,
        date,
        notes,
        order_ids: selectedOrders
      });
      navigate(`/trips/${newTrip.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al crear el viaje. Intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/trips'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Viajes
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Armar Nuevo Viaje
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
          <IconButton onClick={() => navigate('/trips')} sx={{ bgcolor: 'white', border: '1px solid #d0dbe7', color: '#0e141b' }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#0e141b', 
                fontSize: { xs: '1.875rem', md: '2.25rem' }, 
                fontWeight: 900, 
                mb: 0.5,
                letterSpacing: '-0.033em'
              }}
            >
              Nuevo Viaje
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>
              Selecciona una ciudad y los pedidos confirmados para organizar la logística.
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '0.5rem' }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 4 }}>
          {/* Form Side */}
          <Box>
            <Paper sx={{ p: 3, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#0e141b' }}>Configuración del Viaje</Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  select
                  label="Ciudad / Ruta"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  disabled={loadingCities}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RouteIcon sx={{ color: '#4e7397' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.name}>{city.name}</MenuItem>
                  ))}
                  {cities.length === 0 && !loadingCities && (
                    <MenuItem value="" disabled>No hay ciudades configuradas</MenuItem>
                  )}
                </TextField>

                <TextField
                  type="date"
                  label="Fecha Programada"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                  }}
                />

                <TextField
                  label="Notas para el Repartidor"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Ej: Cobrar en efectivo, cliente no atiende de 12 a 14..."
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                  }}
                />

                <Box sx={{ pt: 2 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleCreateTrip}
                    disabled={saving || selectedOrders.length === 0 || !selectedCity}
                    sx={{ 
                      bgcolor: '#800020', 
                      '&:hover': { bgcolor: '#600010' },
                      borderRadius: '0.5rem',
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: 'none'
                    }}
                  >
                    {saving ? 'Generar Viaje...' : 'Confirmar y Generar Viaje'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Orders Side */}
          <Box>
            {selectedCity ? (
              <Paper sx={{ borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #d0dbe7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0e141b' }}>
                    Pedidos en {selectedCity}
                  </Typography>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                    <span style={{ fontWeight: 700, color: '#0e141b' }}>{selectedOrders.length}</span> seleccionados
                  </Typography>
                </Box>

                {loadingOrders ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 8 }}>
                    <CircularProgress sx={{ color: '#800020', mb: 2 }} />
                    <Typography sx={{ color: '#4e7397' }}>Cargando pedidos disponibles...</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ bgcolor: '#f6f7f8' }}>
                            <Checkbox
                              checked={selectedOrders.length === orders.length && orders.length > 0}
                              indeterminate={selectedOrders.length > 0 && selectedOrders.length < orders.length}
                              onChange={(e) => setSelectedOrders(e.target.checked ? orders.map(o => o.id) : [])}
                              sx={{ color: '#d0dbe7', '&.Mui-checked': { color: '#800020' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ bgcolor: '#f6f7f8', color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Pedido</TableCell>
                          <TableCell sx={{ bgcolor: '#f6f7f8', color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Cliente</TableCell>
                          <TableCell sx={{ bgcolor: '#f6f7f8', color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Peso</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                              <Typography sx={{ color: '#4e7397' }}>No hay pedidos confirmados para esta ciudad.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          orders.map((order) => (
                            <TableRow key={order.id} hover onClick={() => handleToggleOrder(order.id)} sx={{ cursor: 'pointer' }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedOrders.includes(order.id)}
                                  onChange={() => handleToggleOrder(order.id)}
                                  sx={{ color: '#d0dbe7', '&.Mui-checked': { color: '#800020' } }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>#{order.order_number}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.875rem' }}>{order.client?.name}</Typography>
                                <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>{order.client?.delivery_address}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, color: '#1b5e20' }}>{order.total_weight} kg</Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, border: '2px dashed #d0dbe7', borderRadius: '1rem' }}>
                <RouteIcon sx={{ fontSize: 48, color: '#d0dbe7', mb: 2 }} />
                <Typography sx={{ color: '#4e7397', textAlign: 'center' }}>Selecciona una ciudad para ver los pedidos disponibles para el viaje.</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TripCreatePage;
